import * as z from 'zod'
import { PositivePercentageRangeSchema, TimestampMetadataSchema, transformUndefinedToNull } from '../_common' // <-- Assumes import is present
import { FREE_PLAN, PaidPlanSchema } from '../_plan'
import { DateTimeSchema } from '../datetime'
import { createId } from '@paralleldrive/cuid2'

const BranchInvestmentSchema = z.object({
  investmentAmount: z.number()
    .min(0)
    .nullish()
    .transform(transformUndefinedToNull),
  investmentSlots: z.int()
    .min(0)
    .nullish()
    .transform(transformUndefinedToNull),
  investorSharePercentage: PositivePercentageRangeSchema
    .nullish()
    .transform(transformUndefinedToNull),
})

const CommonBaseBranchSchema = z.object({
  id: z.string().nonempty(),
  accountId: z.string().nonempty(),

  // --- THIS IS THE FIX FROM JOB 7 ---
  brandId: z.string()
    .nullish() // This allows the field to be string, null, or undefined
    .transform(transformUndefinedToNull), // This ensures 'undefined' becomes 'null'
  // --- END FIX ---

  branchName: z.string().nonempty(),
  isDefaultName: z.boolean().optional(),

  investment: BranchInvestmentSchema
    .nullish()
    .transform((v) => v || BranchInvestmentSchema.parse({})),

  ...TimestampMetadataSchema.shape,
})

/**
 * [@]: `/branches/{branchId}`
 */
export const BranchSchema = z.discriminatedUnion('planType', [
  z.object({
    planType: z.literal(FREE_PLAN),
    currentSubscriptionId: z.null().catch(null),
    ...CommonBaseBranchSchema.shape,
  }),
  z.object({
    planType: PaidPlanSchema,
    currentSubscriptionId: z.string().nonempty(),
    ...CommonBaseBranchSchema.shape,
  }),
])

export type LooseBranch = z.input<typeof BranchSchema>
export type Branch = z.output<typeof BranchSchema>

/**
 * [@]: `/memberships/{membershipId}`
 */
export const BranchMembershipSchema = z.object({
  userId: z.string().nonempty(),
  branchId: z.string().nonempty(),
  role: z.enum(['owner', 'member']).default('member'),

  ...TimestampMetadataSchema.shape,
})

export type LooseBranchMembership = z.input<typeof BranchMembershipSchema>
export type BranchMembership = z.output<typeof BranchMembershipSchema>

export const MemberInvitationParamsSchema = z.object({
  branchId: z.string().nonempty(),
})

/**
 * Generate invitation ID and code.
 */
export function generateMemberInvitationIdAndCode(
  params: z.infer<typeof MemberInvitationParamsSchema>,
) {
  const invitationId = createId()

  return {
    invitationId,
    code: `${params.branchId}.${invitationId}`,
  }
}

/**
 * [@]: `/branches/{branchId}/invites/{invitationId}`
 */
export const BranchMembershipInvitationSchema = z.object({
  branchId: z.string().nonempty(),

  email: z.email(),
  code: z.string().nonempty(),

  expiresAt: DateTimeSchema,
  acceptedAt: DateTimeSchema.optional(),
  canceledAt: DateTimeSchema.optional(),
  canceledBy: z.string().optional(),

  ...TimestampMetadataSchema.shape,
})

export type LooseBranchMembershipInvitation = z.input<typeof BranchMembershipInvitationSchema>
export type BranchMembershipInvitation = z.output<typeof BranchMembershipInvitationSchema>

/**
 * Invitation status constants
 */
export const INVITATION_STATUS = {
  PENDING: 'pending',
  ACCEPTED: 'accepted',
  EXPIRED: 'expired',
  CANCELED: 'canceled',
} as const

export type InvitationStatus = typeof INVITATION_STATUS[keyof typeof INVITATION_STATUS]

/**
 * Get the current status of an invitation
 */
export function getInvitationStatus(invitation: BranchMembershipInvitation): InvitationStatus {
  // Check if canceled
  if (invitation.canceledAt) {
    return INVITATION_STATUS.CANCELED
  }

  // Check if accepted
  if (invitation.acceptedAt) {
    return INVITATION_STATUS.ACCEPTED
  }

  // Check if expired
  const now = new Date()
  const expiresAt = invitation.expiresAt instanceof Date
    ? invitation.expiresAt
    : (invitation.expiresAt as any).toDate
      ? (invitation.expiresAt as any).toDate()
      : new Date(invitation.expiresAt as any)

  if (expiresAt < now) {
    return INVITATION_STATUS.EXPIRED
  }

  // Otherwise it's pending
  return INVITATION_STATUS.PENDING
}